import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { pricingAPI } from "@/lib/api";

export const Pricing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [packages, setPackages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await pricingAPI.getPublicPackages();
        if (response.success && response.data && response.data.length > 0) {
          setPackages(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch public packages:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case "basic":
        return "border-blue-500/50 hover:border-blue-500";
      case "pro":
        return "border-purple-500/50 hover:border-purple-500";
      case "enterprise":
        return "border-yellow-500/50 hover:border-yellow-500";
      default:
        return "border-border";
    }
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case "basic":
        return "bg-blue-500";
      case "pro":
        return "bg-purple-500";
      case "enterprise":
        return "bg-yellow-500";
      default:
        return "bg-primary";
    }
  };

  const handleGetStarted = (planId: string) => {
    // Navigate to auth page with plan selection
    navigate(`/auth?plan=${planId}`);
  };

  const hasDbPackages = packages.length > 0;

  const displayPackages = !hasDbPackages
    ? []
    : packages.map(pkg => {
        const mainService = pkg.services && pkg.services[0];
        let basePriceDecimal = 0;
        let overridePriceDecimal;

        if (mainService && mainService.base_price) {
          basePriceDecimal = mainService.base_price.amountDecimal !== undefined
            ? mainService.base_price.amountDecimal
            : mainService.base_price.amountMinor / 100;
        }

        if (pkg.active_override) {
          overridePriceDecimal = pkg.active_override.amountDecimal !== undefined
            ? pkg.active_override.amountDecimal
            : pkg.active_override.amountMinor / 100;
        }

        const price = billingCycle === 'annual' ? (basePriceDecimal * 10) : basePriceDecimal;
        const activeOverridePrice = overridePriceDecimal !== undefined ? (billingCycle === 'annual' ? (overridePriceDecimal * 10) : overridePriceDecimal) : undefined;

        return {
          ...pkg,
          synthesizedPrice: price,
          synthesizedOverridePrice: activeOverridePrice
        };
      });

  // Map packages from DB
  const displayPlans = displayPackages.map((pkg, index) => {
        const mainService = pkg.services && pkg.services[0];
        const price = pkg.synthesizedPrice !== undefined
          ? pkg.synthesizedPrice
          : (mainService ? mainService.base_price.amountDecimal : 0);
        const currency = mainService ? mainService.base_price.currency : "SAR";
        
        // Prefer explicit tier from admin packages, sequentially fallback if missing
        const tiers = ["basic", "pro", "enterprise"];
        const tier = pkg.tier || tiers[index % tiers.length];
        
        // Read features explicitly from DB
        const features = pkg.features || [];

        const description = pkg.description || t("pricing.proDesc");
          
        return {
          id: pkg._id,
          name: pkg.name,
          description: description,
          price,
          overridePrice: pkg.synthesizedOverridePrice,
          currency,
          features,
          tier,
          popular: pkg.popular !== undefined ? pkg.popular : (tier === "pro"),
        };
      });

  if (isLoading) {
    return (
      <section className="relative flex min-h-[400px] flex-col items-center justify-center px-4 py-24 sm:px-6">
        <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">{t("common.loading")}</p>
      </section>
    );
  }

  if (displayPlans.length === 0) {
    return null;
  }

  return (
    <section className="relative px-4 py-24 sm:px-6">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16 animate-fade-in">
          <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20">
            {t('pricing.badge')}
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {t('pricing.title')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Billing Toggle (Always visible) */}
        <div className="flex items-center justify-center gap-4 mb-12 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <span className={`text-sm font-medium ${billingCycle === "monthly" ? "text-foreground" : "text-muted-foreground"}`}>
            {t('pricing.monthly')}
          </span>
          <button
            onClick={() => setBillingCycle(billingCycle === "monthly" ? "annual" : "monthly")}
            className="relative w-14 h-7 bg-secondary rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <span
              className={`absolute top-1 left-1 w-5 h-5 bg-primary rounded-full transition-transform ${
                billingCycle === "annual" ? "translate-x-7" : ""
              }`}
            />
          </button>
          <span className={`text-sm font-medium ${billingCycle === "annual" ? "text-foreground" : "text-muted-foreground"}`}>
            {t('pricing.annual')}
          </span>
          {billingCycle === "annual" && (
            <Badge className="bg-green-500/20 text-green-500 hover:bg-green-500/30">
              {t('pricing.save20')}
            </Badge>
          )}
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
          {displayPlans.map((plan, index) => (
            <Card
              key={plan.id}
              className={`relative bg-card/50 backdrop-blur-sm border-2 ${getTierColor(
                plan.tier
              )} transition-all hover:shadow-2xl hover:scale-105 animate-scale-in flex flex-col ${
                plan.popular ? "md:-mt-4 md:mb-4" : ""
              }`}
              style={{ animationDelay: `${0.2 + index * 0.1}s` }}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {t('pricing.mostPopular')}
                </Badge>
              )}
              
              <CardHeader className="text-center pb-8">
                <Badge className={`${getTierBadgeColor(plan.tier)} text-white w-fit mx-auto mb-4`}>
                  {plan.tier.toUpperCase()}
                </Badge>
                <CardTitle className="text-2xl mb-2 break-words max-w-full">{plan.name}</CardTitle>
                <CardDescription className="text-base min-h-[48px]">{plan.description}</CardDescription>
                <div className="mt-6 flex flex-col items-center">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-3xl font-bold">
                      {plan.currency === "USD" ? "$" : ""}
                      {plan.overridePrice !== undefined
                        ? plan.overridePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : (typeof plan.price === 'number'
                          ? plan.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : plan.price)}
                      {plan.currency !== "USD" ? ` ${plan.currency}` : ""}
                    </span>
                    <span className="text-muted-foreground">
                      /{billingCycle === "monthly" ? t('pricing.month') : t('pricing.year')}
                    </span>
                  </div>
                  {plan.overridePrice !== undefined && (
                    <div className="text-lg text-muted-foreground/50 line-through mt-0.5">
                      {plan.currency === "USD" ? "$" : ""}
                      {typeof plan.price === 'number'
                          ? plan.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : plan.price}
                      {plan.currency !== "USD" ? ` ${plan.currency}` : ""}
                    </div>
                  )}
                  {billingCycle === "annual" && (
                    <p className="text-sm text-muted-foreground mt-2">
                      {plan.currency === "USD" ? "$" : ""}
                      {plan.overridePrice !== undefined
                        ? (plan.overridePrice / 12).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : (typeof plan.price === 'number'
                          ? (plan.price / 12).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : (parseFloat(plan.price) / 12).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }))}
                      {" "}{t('pricing.perMonth')}
                      {plan.currency !== "USD" ? ` ${plan.currency}` : ""}
                    </p>
                  )}
                </div>
              </CardHeader>

              <CardContent className="flex-1">
                <div className="max-h-[240px] overflow-y-auto pr-2 space-y-4 scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm break-words">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>

              <CardFooter className="mt-auto">
                <Button
                  className="w-full"
                  variant={plan.popular ? "default" : "outline"}
                  onClick={() => handleGetStarted(plan.id)}
                >
                  {t('pricing.getStarted')}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="text-center mt-16 animate-fade-in" style={{ animationDelay: "0.5s" }}>
          <p className="text-muted-foreground mb-4">
            {t('pricing.allPlansInclude')}
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              <span>{t('pricing.cancelAnytime')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              <span>{t('pricing.securePayment')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              <span>{t('pricing.dataProtection')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
